import { z } from 'zod';
import { MAX_FILE_NAME } from '../files.ts';
import { defineCapability, type CapabilityDef } from './capabilities.ts';

// The base capabilities every ITC Web worker provides (design §6.6). The worker
// implements them in @itc/discord. Each that changes something in Discord takes an
// optional audit-log `reason`; the worker adds the acting user to it.

/** A Discord snowflake: a user, role, channel, message or event id. */
export const Snowflake = z.string().regex(/^[0-9]{1,32}$/, 'must be a Discord id');

/** Free text shown in Discord's audit log. */
const Reason = z.string().max(400).optional();

/** A guild member as the worker sees them. */
export const GuildMember = z.object({
  discordId: Snowflake,
  username: z.string(),
  /** The server nickname, else the global name, else the username. */
  displayName: z.string(),
  /** The avatar hash, or '' for none. */
  avatar: z.string(),
  roles: z.array(Snowflake),
  /** Still in membership screening. */
  pending: z.boolean(),
  /** An application's bot account. Left out by a worker that doesn't say; absent reads as a person. */
  bot: z.boolean().optional(),
});
export type GuildMember = z.infer<typeof GuildMember>;

/** A button that sends the press back to ITC Web: `customId` is `<module>:<action>:<id>` (design §6.6). */
export const CustomIdButton = z.object({
  customId: z.string().min(1).max(100),
  label: z.string().min(1).max(80),
  style: z.enum(['primary', 'secondary', 'success', 'danger']).default('primary'),
  disabled: z.boolean().default(false),
});
export type CustomIdButton = z.infer<typeof CustomIdButton>;

/** A button in a message: one that sends the press back, or a link. */
export const MessageButton = z.union([CustomIdButton, z.object({ url: z.url(), label: z.string().min(1).max(80) })]);
export type MessageButton = z.infer<typeof MessageButton>;

/** At most 5 rows of at most 5 buttons. */
export const ButtonRows = z.array(z.array(MessageButton).min(1).max(5)).max(5);

/** Where a message goes: a channel, a thread, or a user's DMs. */
export const MessageTarget = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('channel'), channelId: Snowflake }),
  z.object({ kind: z.literal('thread'), threadId: Snowflake }),
  z.object({ kind: z.literal('user'), discordId: Snowflake }),
]);
export type MessageTarget = z.infer<typeof MessageTarget>;

export const GuildEventStatus = z.enum(['scheduled', 'active', 'completed', 'canceled']);

/** A Discord scheduled event. */
export const GuildEvent = z.object({
  id: Snowflake,
  name: z.string(),
  description: z.string(),
  start: z.iso.datetime(),
  end: z.iso.datetime().nullable(),
  /** The voice or stage channel, or null for an external event. */
  channelId: Snowflake.nullable(),
  /** The location of an external event. */
  location: z.string().nullable(),
  status: GuildEventStatus,
  /** Its page on Discord. */
  url: z.string(),
});
export type GuildEvent = z.infer<typeof GuildEvent>;

const Ok = z.object({ ok: z.literal(true) });

export const GuildSnapshot = defineCapability({
  name: 'guild.snapshot',
  payload: z.object({}),
  result: z.object({ members: z.array(GuildMember) }),
  idempotent: true,
  // Large, and only needed by the reconcile that asked for it.
  storeResult: false,
});

/** A guild role, for pickers and showing role names. */
export const GuildRole = z.object({
  id: Snowflake,
  name: z.string(),
  /** Higher is above. */
  position: z.number().int(),
  /** Managed by an integration (a bot's own role, a booster role): it can't be assigned. */
  managed: z.boolean(),
  /** The role's color as a number, 0 for none. */
  color: z.number().int(),
  /** Whether the role is shown apart in the member list (plan 6F), so a module can tell whether a `roles.edit` is needed before sending one. */
  hoist: z.boolean(),
});
export type GuildRole = z.infer<typeof GuildRole>;

export const ChannelKind = z.enum(['text', 'announcement', 'forum', 'voice', 'stage', 'category', 'other']);
export type ChannelKind = z.infer<typeof ChannelKind>;

/** A guild channel, with what the bot may do in it after role order and overwrites. */
export const GuildChannel = z.object({
  id: Snowflake,
  name: z.string(),
  type: ChannelKind,
  parentId: Snowflake.nullable(),
  position: z.number().int(),
  bot: z.object({ view: z.boolean(), send: z.boolean(), sendInThreads: z.boolean(), connect: z.boolean(), moveMembers: z.boolean(), manageChannels: z.boolean() }),
});
export type GuildChannel = z.infer<typeof GuildChannel>;

/** The guild's roles, without @everyone (read-only, plan 1C Q5 option A). */
export const GuildRoles = defineCapability({
  name: 'guild.roles',
  payload: z.object({}),
  result: z.object({ roles: z.array(GuildRole) }),
  idempotent: true,
  storeResult: false,
});

/** The guild's channels and the bot's effective permissions in each (read-only). */
export const GuildChannels = defineCapability({
  name: 'guild.channels',
  payload: z.object({}),
  result: z.object({ channels: z.array(GuildChannel) }),
  idempotent: true,
  storeResult: false,
});

/** A member's presence, as Discord sends it: `offline` is also invisible. */
export const PresenceStatus = z.enum(['online', 'idle', 'dnd', 'offline']);
export type PresenceStatus = z.infer<typeof PresenceStatus>;

/** A member in a voice channel, and that channel's category (null when it has none). */
export const VoiceActivity = z.object({ discordId: Snowflake, channelId: Snowflake, categoryId: Snowflake.nullable() });
export type VoiceActivity = z.infer<typeof VoiceActivity>;

/** A member's status and the names of what they're playing. */
export const PresenceActivity = z.object({ discordId: Snowflake, status: PresenceStatus, games: z.array(z.string()) });
export type PresenceActivity = z.infer<typeof PresenceActivity>;

/**
 * Who is in voice now and, if the worker has the presence intent, each member's status
 * and game (plan 5E). Both lists are read from what the worker holds, not asked of Discord.
 * `voice` lists everyone in a voice channel, so a member with no entry is not in one.
 * `presences` is empty when `presence` is false, and then says nothing about anyone; when
 * it is true, a member with no entry is offline, as Discord sends no presence for them.
 */
export const GuildActivity = defineCapability({
  name: 'guild.activity',
  payload: z.object({}),
  result: z.object({ presence: z.boolean(), voice: z.array(VoiceActivity), presences: z.array(PresenceActivity) }),
  idempotent: true,
  // Changes by the second, and only needed by the read that asked for it.
  storeResult: false,
});

export const MemberGet = defineCapability({
  name: 'member.get',
  payload: z.object({ discordId: Snowflake }),
  result: z.object({ member: GuildMember.nullable() }),
  idempotent: true,
});

/** Sets a member's server nickname, or clears it with `null`. */
export const MemberSetNickname = defineCapability({
  name: 'member.set_nickname',
  payload: z.object({ discordId: Snowflake, nickname: z.string().min(1).max(32).nullable(), reason: Reason }),
  /** `applied: false` when Discord refuses: the member is the owner or above the bot, or the bot may not rename. Not a failure. */
  result: z.object({ applied: z.boolean() }),
  idempotent: true,
});

export const RolesAdd = defineCapability({
  name: 'roles.add',
  payload: z.object({ discordId: Snowflake, roleId: Snowflake, reason: Reason }),
  result: Ok,
  idempotent: true,
});

export const RolesRemove = defineCapability({
  name: 'roles.remove',
  payload: z.object({ discordId: Snowflake, roleId: Snowflake, reason: Reason }),
  result: Ok,
  idempotent: true,
});

/** Makes a role with no permissions, at the bottom of the order. */
export const RolesCreate = defineCapability({
  name: 'roles.create',
  // No permissions: a role made here only exists to be mentioned or held.
  payload: z.object({ name: z.string().min(1).max(100), mentionable: z.boolean(), reason: Reason }),
  result: z.object({ roleId: Snowflake }),
  // A retry would make a second role.
  idempotent: false,
});

/**
 * Deletes a role, only if its name starts with `namePrefix`: a wrong id can't delete a real
 * role, as the check is the worker's, on the role as it is now. A role whose name doesn't
 * match fails the command. `deleted: false` when it was already gone.
 */
export const RolesDelete = defineCapability({
  name: 'roles.delete',
  payload: z.object({ roleId: Snowflake, namePrefix: z.string().min(1).max(100), reason: Reason }),
  result: z.object({ deleted: z.boolean() }),
  idempotent: true,
});

/**
 * Changes a role's `hoist` (shown apart in the member list) and its icon, and nothing else
 * about it. `icon` is a guild emoji by id, or `null` to clear it. The worker refuses
 * @everyone and managed roles, and fails the command for an emoji that isn't in the guild.
 */
export const RolesEdit = defineCapability({
  name: 'roles.edit',
  payload: z
    .object({
      roleId: Snowflake,
      hoist: z.boolean().optional(),
      icon: z.object({ emojiId: Snowflake }).nullable().optional(),
      reason: Reason,
    })
    .refine((edit) => edit.hoist !== undefined || edit.icon !== undefined, { message: 'An edit needs hoist or icon', path: ['hoist'] }),
  result: z.object({
    /** The role's hoist after the edit. */
    hoist: z.boolean(),
    /** `unsupported` when the guild lacks role icons (hoist is still applied); `unchanged` when `icon` was not given. */
    icon: z.enum(['set', 'cleared', 'unchanged', 'unsupported']),
  }),
  idempotent: true,
});

/** A custom emoji of the guild. */
export const GuildEmoji = z.object({ id: Snowflake, name: z.string(), animated: z.boolean() });
export type GuildEmoji = z.infer<typeof GuildEmoji>;

/** The guild's custom emojis, for choosing a role icon. A read that fails fails the command: it is never an empty list. */
export const GuildEmojis = defineCapability({
  name: 'guild.emojis',
  payload: z.object({}),
  result: z.object({ emojis: z.array(GuildEmoji) }),
  idempotent: true,
  storeResult: false,
});

export const VoiceMove = defineCapability({
  name: 'voice.move',
  payload: z.object({ discordId: Snowflake, channelId: Snowflake, reason: Reason }),
  /** `moved: false` when the member isn't in a voice channel. */
  result: z.object({ moved: z.boolean() }),
  idempotent: true,
});

/** The most people one `voice.move_many` moves. */
export const MAX_VOICE_MOVES = 100;

/** What happened to one person in a `voice.move_many`. */
export const VoiceMoveOutcome = z.object({
  discordId: Snowflake,
  /**
   * `not_in_voice`: Discord says they aren't in a voice channel, or have left the guild, or left voice just before the move (its code 40032). `elsewhere`: the move asked for
   * `fromParentId` and they were in a voice channel in another category (or one whose category can't be told), so they were left alone. Any other error is `failed`.
   */
  outcome: z.enum(['moved', 'already_there', 'not_in_voice', 'elsewhere', 'failed']),
  /** For `failed`: Discord's error code, or a short word when it had none. */
  code: z.string().optional(),
});
export type VoiceMoveOutcome = z.infer<typeof VoiceMoveOutcome>;

/**
 * Moves up to 100 people, each to a channel of their own, a little apart, and answers for
 * each one. One person failing doesn't fail the command. Someone already in their channel is
 * left alone, so running it again moves no one twice.
 *
 * `fromParentId`: only people now in a voice channel of that category are moved. Someone in a
 * voice channel of another category (or at the top, or whose channel can't be read as one) is left
 * alone and answered `elsewhere`; someone in no voice channel is still `not_in_voice`, and an error
 * reading the channel is `failed`, never `elsewhere` or `not_in_voice`. It is checked for each person
 * at the moment of their move, from where the worker sees them then. Left out, anyone in voice moves.
 */
export const VoiceMoveMany = defineCapability({
  name: 'voice.move_many',
  payload: z.object({
    moves: z
      .array(z.object({ discordId: Snowflake, channelId: Snowflake }))
      .min(1)
      .max(MAX_VOICE_MOVES)
      .refine((moves) => new Set(moves.map((move) => move.discordId)).size === moves.length, { message: 'A person can be moved once' }),
    fromParentId: Snowflake.optional(),
    reason: Reason,
  }),
  result: z.object({ results: z.array(VoiceMoveOutcome) }),
  idempotent: true,
});

export const VoiceCreateChannel = defineCapability({
  name: 'voice.create_channel',
  payload: z.object({
    /** The caller's key for this channel: a retry with the same key reuses the channel it created. */
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(100),
    /** The category to create it in. */
    parentId: Snowflake.optional(),
    userLimit: z.number().int().min(0).max(99).optional(),
    reason: Reason,
  }),
  result: z.object({
    channelId: Snowflake,
    created: z.boolean(),
    /** The channel was found by its name, not made or remembered by this key. Absent from an older worker: read as false. */
    adopted: z.boolean().default(false),
  }),
  idempotent: true,
});

/**
 * Deletes a voice or stage channel, and refuses any other kind (the command fails), as a wrong
 * id can't be allowed to delete a text channel. `parentId`: the channel must be in that category,
 * else it fails. `ifEmpty`: not if anyone is in it now, and then the answer is `notEmpty: true`.
 * Bots don't count as anyone. `deleted: false` alone when it was already gone.
 */
export const VoiceDeleteChannel = defineCapability({
  name: 'voice.delete_channel',
  payload: z.object({ channelId: Snowflake, parentId: Snowflake.optional(), ifEmpty: z.boolean().optional(), reason: Reason }),
  result: z.object({ deleted: z.boolean(), notEmpty: z.boolean().optional() }),
  idempotent: true,
});

/** Who's in a voice channel now, without bots. No event says this, as nobody moved. */
export const VoiceMembers = defineCapability({
  name: 'voice.members',
  payload: z.object({ channelId: Snowflake }),
  result: z.object({ discordIds: z.array(Snowflake) }),
  idempotent: true,
});

/**
 * Who a message may ping. Nothing else is ever parsed: `@everyone`, `@here` and
 * mentions in the content that aren't listed here don't ping (Discord's limit is 100 each).
 */
export const MessageMentions = z.object({
  roles: z.array(Snowflake).max(100).optional(),
  users: z.array(Snowflake).max(100).optional(),
});
export type MessageMentions = z.infer<typeof MessageMentions>;

/** The most files Discord takes on one message. */
export const MAX_MESSAGE_FILES = 10;

/** A stored file to attach, by its core_files id: the worker fetches the bytes, so a command only ever carries ids. */
export const MessageFile = z.object({ id: z.string().regex(/^[a-z0-9]{15}$/), name: z.string().min(1).max(MAX_FILE_NAME) });
export type MessageFile = z.infer<typeof MessageFile>;

export const MessagesSend = defineCapability({
  name: 'messages.send',
  payload: z
    .object({
      target: MessageTarget,
      /** Optional only with files: Discord refuses a message with neither. */
      content: z.string().min(1).max(2000).optional(),
      buttons: ButtonRows.optional(),
      mentions: MessageMentions.optional(),
      files: z.array(MessageFile).min(1).max(MAX_MESSAGE_FILES).optional(),
    })
    .refine((message) => message.content !== undefined || message.files !== undefined, { message: 'A message needs content or a file', path: ['content'] }),
  result: z.object({ channelId: Snowflake, messageId: Snowflake }),
  // Sending twice posts twice.
  idempotent: false,
});

export const MessagesEdit = defineCapability({
  name: 'messages.edit',
  payload: z
    .object({
      channelId: Snowflake,
      messageId: Snowflake,
      /** `''` clears the text, which needs a file to leave the message holding something. */
      content: z.string().max(2000).optional(),
      /** Replaces the buttons; `[]` removes them. */
      buttons: ButtonRows.optional(),
      mentions: MessageMentions.optional(),
      /** Replaces the attachments; `[]` removes them. Left out, they stay as they are. */
      files: z.array(MessageFile).max(MAX_MESSAGE_FILES).optional(),
    })
    .refine((message) => message.content !== '' || (message.files ?? []).length > 0, { message: "An edit can't leave a message empty", path: ['content'] }),
  result: Ok,
  idempotent: true,
});

export const MessagesDelete = defineCapability({
  name: 'messages.delete',
  payload: z.object({ channelId: Snowflake, messageId: Snowflake, reason: Reason }),
  result: z.object({ deleted: z.boolean() }),
  idempotent: true,
});

export const ThreadsCreate = defineCapability({
  name: 'threads.create',
  payload: z
    .object({
      channelId: Snowflake,
      name: z.string().min(1).max(100),
      /** Start the thread from this message, rather than as a standalone thread. */
      messageId: Snowflake.optional(),
      autoArchiveMinutes: z.union([z.literal(60), z.literal(1440), z.literal(4320), z.literal(10080)]).optional(),
      /** Only those added to it, and moderators, see it. Not from a message, and only in a text channel. */
      private: z.boolean().optional(),
      reason: Reason,
    })
    .refine((thread) => thread.private !== true || thread.messageId === undefined, { message: "A private thread can't start from a message", path: ['private'] }),
  result: z.object({ threadId: Snowflake }),
  idempotent: false,
});

export const ThreadsArchive = defineCapability({
  name: 'threads.archive',
  payload: z.object({ threadId: Snowflake, reason: Reason }),
  result: Ok,
  idempotent: true,
});

/** Adds a member to a thread, so a private one shows to them. Adding someone who is already in it changes nothing. Discord's audit log takes no reason for it. */
export const ThreadsAddMember = defineCapability({
  name: 'threads.add_member',
  payload: z.object({ threadId: Snowflake, discordId: Snowflake }),
  result: Ok,
  idempotent: true,
});

/** The most `messages.recent` returns. */
export const MAX_RECENT_MESSAGES = 50;

/** A message as `messages.recent` shows it. No text: who wrote it, who it mentions and when. */
export const RecentMessage = z.object({
  messageId: Snowflake,
  authorDiscordId: Snowflake,
  /** The author's guild role ids, without @everyone. Empty for someone no longer in the guild, and for a webhook. */
  authorRoles: z.array(Snowflake),
  /** The users the message mentions directly. Role and @everyone mentions are not listed. */
  mentionedDiscordIds: z.array(Snowflake),
  createdAt: z.iso.datetime(),
});
export type RecentMessage = z.infer<typeof RecentMessage>;

/**
 * Who posted in a channel or thread lately, newest first, without what they wrote (plan 5D).
 * The worker has no Message Content intent, so a message's text is never in what Discord
 * sends it; this reads only the authors and mentions Discord does send.
 */
export const MessagesRecent = defineCapability({
  name: 'messages.recent',
  payload: z.object({
    channelId: Snowflake,
    limit: z.number().int().min(1).max(MAX_RECENT_MESSAGES),
    /** Only messages created after this moment. */
    after: z.iso.datetime().optional(),
  }),
  result: z.array(RecentMessage),
  idempotent: true,
  // Needed by the caller that asked, once.
  storeResult: false,
});

export const GuildEventsList = defineCapability({
  name: 'guild_events.list',
  payload: z.object({}),
  result: z.object({ events: z.array(GuildEvent) }),
  idempotent: true,
});

export const GuildEventsGet = defineCapability({
  name: 'guild_events.get',
  payload: z.object({ eventId: Snowflake }),
  result: z.object({ event: GuildEvent.nullable() }),
  idempotent: true,
});

const GuildEventFields = {
  name: z.string().min(1).max(100),
  description: z.string().max(1000).optional(),
  start: z.iso.datetime(),
  end: z.iso.datetime().optional(),
  /** A voice or stage channel; omit and give `location` for an external event, which then needs `end`. */
  channelId: Snowflake.optional(),
  location: z.string().min(1).max(100).optional(),
};

export const GuildEventsCreate = defineCapability({
  name: 'guild_events.create',
  payload: z.object({ ...GuildEventFields, reason: Reason }),
  result: z.object({ event: GuildEvent }),
  idempotent: false,
});

/** `status` starts or ends an event: Discord does both through an edit, and `guild_events.cancel` cancels one. */
export const GuildEventsEdit = defineCapability({
  name: 'guild_events.edit',
  payload: z.object({ eventId: Snowflake, ...z.object(GuildEventFields).partial().shape, status: z.enum(['active', 'completed']).optional(), reason: Reason }),
  result: z.object({ event: GuildEvent }),
  idempotent: true,
});

export const GuildEventsCancel = defineCapability({
  name: 'guild_events.cancel',
  payload: z.object({ eventId: Snowflake, reason: Reason }),
  result: Ok,
  idempotent: true,
});

/** The most `guild_events.users` returns. */
export const GUILD_EVENT_USERS_CAP = 500;

/** Discord's page size for an event's subscribers. */
export const GUILD_EVENT_USERS_PAGE = 100;

/** Who's interested in an event, in id order. `capped` says there are more than the cap. */
export const GuildEventsUsers = defineCapability({
  name: 'guild_events.users',
  payload: z.object({ eventId: Snowflake }),
  result: z.object({ discordIds: z.array(Snowflake), capped: z.boolean() }),
  idempotent: true,
});

/** Every base capability, by name. */
export const BASE_CAPABILITIES: readonly CapabilityDef[] = [
  GuildSnapshot,
  GuildRoles,
  GuildChannels,
  GuildActivity,
  MemberGet,
  MemberSetNickname,
  RolesAdd,
  RolesRemove,
  RolesCreate,
  RolesDelete,
  RolesEdit,
  GuildEmojis,
  VoiceMove,
  VoiceMoveMany,
  VoiceCreateChannel,
  VoiceDeleteChannel,
  VoiceMembers,
  MessagesSend,
  MessagesEdit,
  MessagesDelete,
  ThreadsCreate,
  ThreadsArchive,
  ThreadsAddMember,
  MessagesRecent,
  GuildEventsList,
  GuildEventsGet,
  GuildEventsCreate,
  GuildEventsEdit,
  GuildEventsCancel,
  GuildEventsUsers,
];
